import React, { useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Star, BadgeCheck, ExternalLink, Loader2 } from 'lucide-react';

const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps/search/?api=1&query=Motion+Reality+Unit+9+Croft+Business+Park+Bromborough+CH62+3RA';
const GOOGLE_REVIEW_WRITE_URL = 'https://g.page/r/CSnwEYnMDB_5ECE/review';
const TRUSTPILOT_URL = 'https://uk.trustpilot.com/review/motionreality.co.uk';
const TRUSTPILOT_BUSINESS_UNIT_ID = '6928131014306be10e1542a7';

// Compact 3-up "What Drivers Say" block for the home page — replaces the tall
// full Testimonials carousel. Shows only the three most recent approved reviews
// with a slim Trustpilot bar and a single compact row of review-source links.
export default function HomeReviews() {
  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['approved-reviews'],
    queryFn: () => base44.entities.Review.filter({ status: 'approved' }, '-created_date'),
  });

  useEffect(() => {
    if (TRUSTPILOT_BUSINESS_UNIT_ID && window.Trustpilot?.load) {
      window.Trustpilot.load();
    }
  }, []);

  const featured = reviews.slice(0, 3);

  return (
    <section className="py-16 px-6 lg:px-10 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">TESTIMONIALS</p>
            <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight">WHAT DRIVERS SAY</h2>
          </div>
          {TRUSTPILOT_BUSINESS_UNIT_ID && (
            <div className="flex items-center gap-3">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-primary fill-primary" />
                ))}
              </div>
              <div
                className="trustpilot-widget"
                data-locale="en-GB"
                data-template-id="53aa8807dec7e10d38f59f32"
                data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
                data-style-height="24px"
                data-style-width="100%"
              >
                <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer" className="text-white/40 text-xs font-bold tracking-widest">TRUSTPILOT</a>
              </div>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : featured.length === 0 ? (
          <div className="text-center py-10 border border-white/5 bg-[#111115]">
            <p className="text-white/30 text-sm">Reviews from our customers will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {featured.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-[#111115] border border-white/5 p-6 flex flex-col"
              >
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className={`w-3.5 h-3.5 ${j < (t.rating || 5) ? 'text-primary fill-primary' : 'text-white/10'}`} />
                  ))}
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-5 flex-1 line-clamp-4">"{t.quote}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <div>
                    <p className="text-white font-bold text-xs">{t.author_name}</p>
                    {t.verified ? (
                      <span className="inline-flex items-center gap-1 text-primary/80 text-[9px] font-bold tracking-widest uppercase">
                        <BadgeCheck className="w-3 h-3" /> Verified
                      </span>
                    ) : (
                      <p className="text-white/30 text-[9px] font-bold tracking-widest uppercase">{t.source_label || t.source}</p>
                    )}
                  </div>
                  {t.source_url ? (
                    <a href={t.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary text-[9px] font-bold tracking-widest uppercase hover:text-primary/80">
                      VIEW <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-white/20 text-[9px] font-bold tracking-widest uppercase">ON SITE</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/70 hover:text-white text-[11px] font-bold tracking-widest px-5 py-2.5 transition-colors">
            GOOGLE REVIEWS <ExternalLink className="w-3 h-3" />
          </a>
          <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/70 hover:text-white text-[11px] font-bold tracking-widest px-5 py-2.5 transition-colors">
            TRUSTPILOT <ExternalLink className="w-3 h-3" />
          </a>
          <a href={GOOGLE_REVIEW_WRITE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[11px] font-bold tracking-widest px-5 py-2.5 transition-colors">
            LEAVE A REVIEW <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </section>
  );
}